import { useQuery } from '@tanstack/react-query';
import { ErrorState, FullPageSpinner, type ShellBrand, ThemeProvider, useDocumentBranding } from '@wl/ui';
import { createContext, type ReactNode, useContext } from 'react';
import { api } from './api';
import { useAuth } from './auth';
import type { Branding, SystemStatus } from './types';

const BrandingContext = createContext<Branding | null>(null);

export const BRANDING_KEY = ['branding'] as const;

/**
 * Girişten önce tenant'ın adı, logosu ve teması yüklenir. Oturum açıksa kullanıcının
 * tema tercihi (me.theme.preset) uygulanır.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const q = useQuery({ queryKey: BRANDING_KEY, queryFn: () => api.get<Branding>('/branding'), staleTime: 5 * 60_000 });
  useDocumentBranding({ title: q.data?.appName, faviconUrl: q.data?.faviconUrl });

  if (q.isPending) return <FullPageSpinner />;
  if (q.isError) {
    return (
      <div className="mx-auto max-w-md p-6">
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }
  return (
    <BrandingContext.Provider value={q.data}>
      <ThemeProvider brand={q.data.theme} preset={me?.theme.preset ?? null}>
        {children}
      </ThemeProvider>
    </BrandingContext.Provider>
  );
}

export function useBranding(): Branding {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('BrandingProvider eksik');
  return ctx;
}

export function useShellBrand(): ShellBrand {
  const b = useBranding();
  return { name: b.appName, logoUrl: b.logoUrl, logoDarkUrl: b.logoDarkUrl };
}

/** Bakım ve kapatılmış özellikler (platform kontrolü); bakımda 15 sn, normalde 60 sn'de bir yenilenir. */
export function useSystemStatus() {
  return useQuery({
    queryKey: ['system', 'status'],
    queryFn: () => api.get<SystemStatus>('/system/status'),
    refetchInterval: (q) => (q.state.data?.maintenance ? 15_000 : 60_000),
    staleTime: 15_000,
  });
}
